import React, { useReducer } from "react";
import Dayjs from "dayjs";
import { useParams } from "react-router-dom";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";

// Custom.
import PreviewLayout from "containers/Layout/PreviewLayout";
import { Form, FormTabProps } from "@vocollege/components";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import { GET_DIPLOMA, UPDATE_DIPLOMA, CREATE_DIPLOMA, SEARCH_GROUP } from "api";
import { VoAuth, regexPatterns } from "@vocollege/app";
import { reducer } from "@vocollege/app/dist/modules/VoHelpers";
import { useStyles } from "ui/styles/form";
import DiplomaPreview from "containers/Templates/Diploma/DiplomaPreview";
import { SEARCH_USER } from "@vocollege/app/dist/modules/VoApi";
import { FormProps } from "@vocollege/components/dist/Form";
import { getUserString } from "@vocollege/app/dist/modules/VoHelpers";
import BatchSelection from "components/BatchSelection";
import BatchPersonList, {
  BatchPersonListItem,
} from "components/BatchPersonList";
import {
  getPageTitle,
  getGroupTitle,
  getGroupTitleValue,
} from "services/Utils";
import SaveConsentDialog from "components/SaveConsentDialog";
import { EnhancedTableStatusCell } from "@vocollege/components";

const initialFormState = {
  formLoading: true,
  formState: {
    isDirty: false,
    isValid: false,
  },
  mode: "single",
  creditsHelperText: I18n.get.diploma.helpTexts.credits.ung,
};

const DiplomaEdit: React.FC = () => {
  const initialDate = Dayjs().format("YYYY-MM-DD");
  const urlParams = useParams<any>();
  const { id } = urlParams;
  const classes = useStyles();
  const navigate = useNavigate();
  const [formState, formDispatch] = useReducer(reducer, initialFormState);

  // Methods.

  const getInitialState = () => {
    const initialState: { [key: string]: any } = {
      id: 0,
      diploma_type: "ung",
      firstname: "",
      lastname: "",
      personal_number: "",
      date: initialDate,
      author: null,
      president: "",
      principal: "",
      educator: null,
      education: "vardOchOmsorg",
      education_freetext: "",
      credits: 0,
      done: 0,
      list: [],
    };
    if (id && id === "skapa") {
      initialState.author = {
        id: VoAuth.currentUser.id,
        name: VoAuth.currentUser.name,
        type: "user",
      };
    }
    return initialState;
  };

  const [state, dispatch] = useReducer(reducer, getInitialState());

  const isMultiple = () => {
    return formState.mode === "multiple";
  };

  const handleCancel = () => {
    navigate("/diplom");
  };

  const handleComplete = (obj: any) => {
    if (isMultiple()) {
      navigate("/diplom");
    } else {
      navigate(`/diplom/${obj[Object.keys(obj)[0]].id}`);
    }
  };

  const handleFormChange: FormProps["onFormChange"] = (formState) => {
    formDispatch({ key: "formState", values: formState });
  };

  const getAvailableDiplomaTypeValues = () => {
    return Object.keys(I18n.get.diploma.diplomaTypes).map(
      (diplomaType: string) => ({
        label: I18n.get.diploma.diplomaTypes[diplomaType],
        value: diplomaType,
      })
    );
  };

  const getAvailableEducationValues = () => {
    return Object.keys(I18n.get.diploma.education[state.diploma_type]).map(
      (education: string) => ({
        label: I18n.get.diploma.education[state.diploma_type][education],
        value: education,
      })
    );
  };

  const handleBatchPersonListChange = (items: BatchPersonListItem[]) => {
    dispatch({
      values: {
        firstname: items[0].firstname,
        lastname: items[0].lastname,
        personal_number: items[0].personal_number,
        list: items,
      },
    });
  };

  const getExtraActions = () => {
    return (
      isMultiple() && (
        <>
          <BatchPersonList
            className="vo-batch-person-list-button"
            onChange={handleBatchPersonListChange}
          />
          <div className={classes.grow}></div>
        </>
      )
    );
  };

  const setCreditsHelperText = (type: string) => {
    formDispatch({
      key: "creditsHelperText",
      values: I18n.get.diploma.helpTexts.credits[type],
    });
  };

  const getTabs = () => {
    const tabs: FormTabProps[] = [];

    tabs.push({
      key: "content",
      label: I18n.get.misc.content,
      fields: [
        {
          name: "list",
          label: "",
          type: "custom",
          grid: {
            xs: 12,
          },
          render: (data: any) => <></>,
          overrideValue: state.list,
        },
        {
          name: "diploma_type",
          label: I18n.get.diploma.labels.diplomaType,
          type: "select",
          grid: {
            xs: 12,
            md: 6,
          },
          params: {
            availableValues: getAvailableDiplomaTypeValues,
          },
          onChange: (value: any) => {
            dispatch({ key: "diploma_type", values: value });
            setCreditsHelperText(value);
          },
        },
        {
          name: "credits",
          label: I18n.get.diploma.labels.credits,
          type: "text",
          required: true,
          grid: {
            xs: 12,
            md: 6,
          },
          validation: {
            required: true,
          },
          onChange: (value: any) => {
            dispatch({ key: "credits", values: value });
          },
          params: {
            inputProps: {
              type: "number",
            },
            helperText: formState.creditsHelperText,
          },
        },
        {
          name: "education",
          label: I18n.get.diploma.labels.education,
          type: "select",
          grid: {
            xs: 12,
            md: 8,
          },
          params: {
            availableValues: getAvailableEducationValues,
            dependency: {
              diploma_type: {
                value: ["ung", "vuxen"],
                visibility: true,
              },
            },
          },
          onChange: (value: any) =>
            dispatch({ key: "education", values: value }),
        },
        {
          name: "education_freetext",
          label: I18n.get.diploma.labels.degree,
          type: "text",
          grid: {
            xs: 12,
            md: 8,
          },
          params: {
            dependency: {
              diploma_type: {
                value: ["yh"],
                visibility: true,
              },
            },
          },
          onChange: (value: any) =>
            dispatch({ key: "education_freetext", values: value }),
        },
        {
          name: "date",
          label: I18n.get.misc.date,
          type: "date_field",
          required: true,
          grid: {
            xs: 12,
            md: 4,
          },
          onChange: (value: any) => dispatch({ key: "date", values: value }),
        },
        {
          name: "personal_number",
          label: I18n.get.user.labels.personalNumber,
          type: !isMultiple() ? "text" : "hidden",
          grid: {
            xs: 12,
            lg: 4,
          },
          params: {
            helperText: I18n.get.form.messages.personalNumberWhenPossible,
          },
          validation: {
            validate: (value: any) => {
              if (!value || value === "") {
                return true;
              }
              return (
                !!value.match(regexPatterns.personalNumber) ||
                I18n.get.form.errors.personalNumberInvalid
              );
            },
          },
          onChange: (value: any) =>
            dispatch({ key: "personal_number", values: value }),
        },
        {
          name: "firstname",
          label: I18n.get.user.labels.firstname,
          type: !isMultiple() ? "text" : "hidden",
          required: true,
          grid: {
            xs: 12,
            md: 6,
            lg: 4,
          },
          validation: {
            required: !isMultiple(),
          },
          onChange: (value: any) =>
            dispatch({ key: "firstname", values: value }),
        },
        {
          name: "lastname",
          label: I18n.get.user.labels.lastname,
          type: !isMultiple() ? "text" : "hidden",
          required: true,
          grid: {
            xs: 12,
            md: 6,
            lg: 4,
          },
          validation: {
            required: !isMultiple(),
          },
          onChange: (value: any) =>
            dispatch({ key: "lastname", values: value }),
        },
        {
          name: "educator",
          label: I18n.get.group.categories.educator,
          type: "entity_field",
          required: !VoAuth.ability.can("create_all_diplomas", "Cert"),
          validation: {
            required: VoAuth.ability.can("create_all_diplomas", "Cert")
              ? false
              : I18n.get.form.errors.required,
          },
          grid: {
            xs: 12,
          },
          params: {
            renderFieldValue: getGroupTitleValue,
            dialog: {
              types: ["group"],
              renderTitleField: getGroupTitle,
              extraDetails: (item: any) =>
                item?.status === 0 && (
                  <EnhancedTableStatusCell data={item.status} />
                ),
              query: SEARCH_GROUP,
              category: "searchGroup",
              variables: {
                categories: ["educator"],
              },
            },
          },
          onChange: (value: any) =>
            dispatch({ key: "educator", values: value }),
        },

        {
          name: "president",
          label: I18n.get.diploma.labels.president,
          type: "text",
          grid: {
            xs: 12,
          },
          params: {
            inputProps: {
              autoComplete: "on",
            },
            ...(VoAuth.ability.can("create_all_diplomas", "Cert")
              ? {
                  dependency: {
                    educator: {
                      value: null,
                      visibility: false,
                    },
                  },
                }
              : {}),
          },
          onChange: (value: any) =>
            dispatch({ key: "president", values: value }),
        },
        {
          name: "principal",
          label:
            VoAuth.ability.can("create_all_diplomas", "Cert") && !state.educator
              ? I18n.get.vocertMisc.voCollegeKansli
              : I18n.get.diploma.template.principal,
          type: "text",
          required: true,
          validation: {
            required: I18n.get.form.errors.required,
          },
          grid: {
            xs: 12,
          },
          params: {
            inputProps: {
              autoComplete: "on",
            },
          },
          onChange: (value: any) =>
            dispatch({ key: "principal", values: value }),
        },
        {
          name: "author",
          label: I18n.get.misc.author,
          type: "entity_field",
          grid: {
            xs: 12,
          },
          params: {
            dialog: {
              renderTitleField: (item: any) => getUserString(item),
              types: ["user"],
              query: SEARCH_USER,
              category: "searchUser",
            },
            fields: {
              user: {
                title: "name",
              },
            },
          },
        },
      ],
    });

    return tabs;
  };

  return (
    <>
      <PreviewLayout
        className={classes.root}
        leftColumn={
          <>
            <Alert severity="warning" className={classes.row}>
              {I18n.get.vocertMessages.correctPreview}
            </Alert>
            {!state.uid && !formState.formLoading && !isMultiple() && (
              <Alert severity="error" className={classes.row}>
                {I18n.get.diploma.messages.saveBeforePrint}
              </Alert>
            )}
            {!formState.formLoading && id && id === "skapa" && (
              <BatchSelection
                onChange={(tab) => formDispatch({ key: "mode", values: tab })}
              />
            )}
            <Form
              tabs={getTabs()}
              labels={{
                loading: I18n.get.form.labels.loadingContent,
                create: I18n.get.diploma.labels.createDiploma,
                created: isMultiple()
                  ? I18n.get.diploma.messages.diplomasGenerated
                  : I18n.get.diploma.messages.diplomaSaved,
                updated: I18n.get.diploma.messages.diplomaSaved,
                deleted: I18n.get.diploma.messages.diplomaDeleted,
                fieldRequired: I18n.get.form.errors.required,
              }}
              operations={{
                category: "certDiploma",
                get: GET_DIPLOMA,
                create: CREATE_DIPLOMA,
                update: UPDATE_DIPLOMA,
              }}
              initialState={getInitialState()}
              renderPageTitle={(state) =>
                !isMultiple()
                  ? getPageTitle(state, I18n.get.diploma.labels.createDiploma)
                  : ""
              }
              urlParams={urlParams}
              createParam="skapa"
              classes={{
                toolbar: classes.toolbar,
                paper: classes.form,
                paperRoot: "vo-batch-person-list-form",
              }}
              onFormChange={handleFormChange}
              onQueryLoading={(loading) =>
                formDispatch({ key: "formLoading", values: loading })
              }
              onDataChange={(data, fetchedData) => {
                setCreditsHelperText(data.diploma_type);
                dispatch({
                  values: {
                    ...data,
                    uid: fetchedData.certDiploma.uid,
                  },
                });
              }}
              onComplete={handleComplete}
              onCancel={handleCancel}
              loadQueryOnParamsChange
              toolbarProps={{
                options: {
                  saveButton: {
                    label: isMultiple()
                      ? I18n.get.vocertActions.generate
                      : null,
                  },
                },
                extraActions: getExtraActions(),
              }}
            />
          </>
        }
        rightColumn={
          !formState.formLoading ? (
            <DiplomaPreview
              item={state}
              formState={formState.formState}
              id={id}
            />
          ) : (
            <></>
          )
        }
        rightColumnProps={{
          className: clsx(classes.previewWrapper),
        }}
      />
      {id === "skapa" && <SaveConsentDialog context="Diploma" />}
    </>
  );
};

export default DiplomaEdit;
